import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useAtom } from 'jotai';
import { useEffect } from 'react';

import { authStateAtom } from '@ekhodealer/ekho-common/auth-client';

import type { AdminGetMeResponse, ServicesProvider } from '@ekhodealer/ekho-common';

import { batchGetServicesProviders } from '@common/api/services-providers/useGetServicesProvider';
import { queries } from '@config/server/queryKeys';

/**
 * Hook to fetch all services providers that the user has access to.
 *
 * Uses a single batch request instead of N individual requests.
 * Populates individual query cache entries so that useGetServicesProvider(id)
 * hooks elsewhere in the app get instant cache hits.
 */
export const useGetUserServicesProviders = (): {
	servicesProviders: ServicesProvider[];
	isLoading: boolean;
	hasError: boolean;
	servicesProviderIds: string[];
} => {
	const [authState] = useAtom(authStateAtom);
	const { isAuthenticated, me } = authState;
	const adminMe = me as AdminGetMeResponse | null;
	const queryClient = useQueryClient();

	const servicesProviderIds = adminMe?.allowedServicesProviderIds ?? [];
	const isLoadingIds = isAuthenticated && !me;

	// Create a stable query key from sorted IDs
	const sortedIdsKey = [...servicesProviderIds].sort().join(',');

	const {
		data,
		isLoading: isBatchLoading,
		isError,
	} = useQuery({
		queryKey: queries.servicesProviders.batch(sortedIdsKey).queryKey,
		queryFn: () => batchGetServicesProviders(servicesProviderIds),
		staleTime: Infinity,
		enabled: servicesProviderIds.length > 0,
	});

	// Populate individual SP cache entries so useGetServicesProvider(id) gets cache hits
	useEffect(() => {
		if (!data?.servicesProviders) return;
		for (const sp of data.servicesProviders) {
			queryClient.setQueryData(
				queries.servicesProviders.detail(sp.id).queryKey,
				sp,
			);
		}
	}, [data, queryClient]);

	const servicesProviders = data?.servicesProviders ?? [];
	const isLoading = isLoadingIds || isBatchLoading;
	const allReturnedEmpty =
		servicesProviderIds.length > 0 && !isBatchLoading && servicesProviders.length === 0;
	const hasError = isError || allReturnedEmpty;

	return {
		servicesProviders,
		isLoading,
		hasError,
		servicesProviderIds,
	};
};
